import React, { useEffect } from 'react'
import { Row, Col } from 'react-bootstrap'

import 'styles/AI.css'
import CTA from 'components/CTA'
import PageHeader from 'components/PageHeader'
import Footer from 'components/Footer'
import Icon from 'components/Icon'
import LinkButton from 'components/LinkButton'
import ExternalButton from 'components/ExternalButton'
import ScrollLink from 'components/ScrollLink'

const features = [
  {
    icon: 'ShieldLock',
    title: 'No Integration to Build',
    text: "Connect an MCP Client and You're Live"
  },
  {
    icon: 'Cog',
    title: 'Read & Write',
    text: 'Order Lookups, Updates, and Chargeback Claims from One Connection'
  },
  {
    icon: 'AbsDrone',
    title: 'Any MCP Client',
    text: 'Works with Any MCP-Capable Assistant, or Your Own Support Tooling'
  },
  {
    icon: 'Squiggle',
    title: 'Secure by Design',
    text: 'Every Action Authenticated and Fully Audited'
  }
]

const steps = [
  {
    title: 'Get Your API Key',
    text: 'Talk to our team to get set up, then generate an API key from your dashboard.'
  },
  {
    title: 'Point Your Client',
    text: 'Connect any MCP-capable AI client to the Apruvd endpoint with that key.'
  },
  {
    title: 'Start Asking',
    text: 'Submit orders and check decisions just by asking.'
  }
]

export default function AI() {
  useEffect(() => { window.scrollTo(0, 0) }, [])
  return (
    <div className="AI">
      <PageHeader />
      <article className="welcome">
        <Row>
          <Col md={12} className="tagline">
            <h4><span className="green">New</span> &middot; AI Integration</h4>
            <h1>
              Bring Apruvd Into Your <span className="green">AI Workflows.</span>
            </h1>
          </Col>
        </Row>
        <Row>
          <Col md={8} className="tagline">
            <p>
              The Apruvd <strong>MCP server</strong> connects your fraud operations to any
              MCP-capable AI assistant, so your team can have Apruvd review orders, check
              decisions, and much more{' '}
              <strong><em>right where they already work</em></strong>, in plain language.
            </p>
          </Col>
          <Col md={4} className="slogan">
            <CTA to="/contact" title="Talk to us" text="Request a Quote" />
          </Col>
        </Row>
        <Row className="lm">
          <Col md={12} className="center more">
            <ScrollLink to="#what" offset={-8}>Learn More</ScrollLink>
          </Col>
        </Row>
      </article>

      <article id="what" className="what">
        <Row>
          <Col md={1}></Col>
          <Col md={7}>
            <h2>Fraud Decisions, <span className="green">Where Your AI Already Works.</span></h2>
            <p>
              MCP, the <a href="https://modelcontextprotocol.io" target="_blank" rel="noreferrer">Model Context Protocol</a>,
              is the emerging standard for connecting AI assistants to other tools.
              Point an MCP-capable client at Apruvd and your team can check an order's status,
              submit and update transactions, and get guaranteed decisions without writing
              any code or learning a new dashboard.
            </p>
            <p>
              It's the <strong>fastest way to put Apruvd to work</strong>. Instead of scoping and
              building a custom integration, connect an AI client your team already runs and start
              getting guaranteed decisioning right away, fully audited.
            </p>
          </Col>
          <Col md={4} className="center">
            <div className="what-icon">
              <Icon i="AbsStack" />
            </div>
          </Col>
        </Row>
      </article>

      <article className="features">
        <Row>
          <Col md={12} className="center">
            <h3>Why It Matters<span className="green">.</span></h3>
            <p>Speed, Safety, and Simplicity.</p>
          </Col>
        </Row>
        <Row className="featureset">
          {features.map((feature, i) => (
            <Col md={3} xs={6} className="center" key={i}>
              <div className="icon"><Icon i={feature.icon} /></div>
              <h3>{feature.title}</h3>
              <p>{feature.text}</p>
            </Col>
          ))}
        </Row>
      </article>

      <article id="tools" className="tools">
        <Row>
          <Col md={1}></Col>
          <Col md={10}>
            <h4 className="green">What You Can Do</h4>
            <h2>Manage Your <span className="green">Orders</span>, Just By Asking<span className="green">.</span></h2>
            <p>
              Check status, submit and update orders, and get decisions, all without
              opening a dashboard.
            </p>
            <p className="use-case">
              Wire it into the AI customer-service chatbot inside your own support platform.
              When a rep thinks an order looks suspect, they can send it to Apruvd and get a
              guaranteed decision right in the conversation, so good orders get approved
              instead of canceled.{' '}
              <strong>No portal-hopping, no copy-paste, no waiting.</strong>
            </p>
          </Col>
        </Row>
      </article>

      <article className="get-started">
        <Row>
          <Col md={1}></Col>
          <Col md={10}>
            <h4 className="green">Getting Started</h4>
            <h2>Three Steps<span className="green">.</span></h2>
          </Col>
        </Row>
        <Row className="steps">
          <Col md={1}></Col>
          {steps.map((step, i) => (
            <Col md={3} xs={12} className="step center" key={i}>
              <div className="num green">{i + 1}</div>
              <h3>{step.title}</h3>
              <p>{step.text}</p>
            </Col>
          ))}
        </Row>
        <Row className="cta-row">
          <Col md={12} className="center">
            <ExternalButton to="https://app.apruvd.com/public/documentation/#mcp-server">Read the Docs</ExternalButton>
            <LinkButton to="/contact">Get Started</LinkButton>
          </Col>
        </Row>
      </article>
      <Footer />
    </div>
  )
}
